import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  CheckCircle, 
  User, 
  Phone, 
  Mail, 
  Shield, 
  FileText, 
  MapPin,
  Calendar,
  Star,
  Copy,
  Download,
  ArrowLeft,
  Eye
} from 'lucide-react';

const DriverRegistrationSuccess = ({ driverData, onClose, onViewDrivers }) => {
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const handleDownloadDetails = () => {
    const details = `
Driver Registration Details
==========================

Personal Information:
- Name: ${driverData.name}
- Phone: ${driverData.phone}
- Email: ${driverData.email || 'Not provided'}
- License Number: ${driverData.license_number}
- Experience Level: ${driverData.experience_level}

Registration Details:
- Driver ID: ${driverData.driver_id || 'N/A'}
- Registration Date: ${new Date().toLocaleDateString()}
- Status: Active
- Fleet Operator: ${driverData.operator_id || 'N/A'}
    `;
    
    const blob = new Blob([details], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver-${driverData.name.replace(/\s+/g, '-').toLowerCase()}-details.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getExperienceBadgeColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-blue-100 text-blue-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'experienced': return 'bg-orange-100 text-orange-800';
      case 'expert': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getExperienceLabel = (level) => {
    switch (level) {
      case 'beginner': return 'Beginner (0-1 years)';
      case 'intermediate': return 'Intermediate (1-3 years)';
      case 'experienced': return 'Experienced (3-5 years)';
      case 'expert': return 'Expert (5+ years)';
      default: return level;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Driver Registered Successfully!
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            The driver has been successfully added to your fleet and is now ready to receive assignments.
          </p>
        </div>

        {/* Driver Details Card */}
        <Card className="mb-6 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <User className="w-6 h-6" />
              Driver Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-green-600" />
                  Personal Information
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-semibold">{driverData.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToClipboard(driverData.name)}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Phone</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-semibold">{driverData.phone}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToClipboard(driverData.phone)}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-semibold">
                        {driverData.email || 'Not provided'}
                      </span>
                      {driverData.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyToClipboard(driverData.email)}
                          className="h-6 w-6 p-0"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Professional Information
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">License Number</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-semibold font-mono text-sm">
                        {driverData.license_number}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToClipboard(driverData.license_number)}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Star className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Experience Level</span>
                    </div>
                    <Badge className={getExperienceBadgeColor(driverData.experience_level)}>
                      {getExperienceLabel(driverData.experience_level)}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Registration Date</span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">Status</span>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      Active
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Driver ID Section */}
            {driverData.driver_id && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">ID</span>
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">Driver ID</p>
                      <p className="text-sm text-blue-700">Use this ID for future reference</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold text-blue-900">
                      {driverData.driver_id}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyToClipboard(driverData.driver_id)}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={onViewDrivers}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
          >
            <Eye className="w-4 h-4" />
            View All Drivers
          </Button>
          
          <Button
            onClick={handleDownloadDetails}
            variant="outline"
            className="flex items-center gap-2 border-green-600 text-green-600 hover:bg-green-50 px-6 py-3"
          >
            <Download className="w-4 h-4" />
            Download Details
          </Button>
          
          <Button
            onClick={onClose}
            variant="outline"
            className="flex items-center gap-2 border-gray-600 text-gray-600 hover:bg-gray-50 px-6 py-3"
          >
            <ArrowLeft className="w-4 h-4" />
            Close
          </Button>
        </div>

        {/* Next Steps */}
        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Next Steps</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-600 font-bold text-xs">1</span>
              </div>
              <div>
                <p className="font-medium">Driver Notification</p>
                <p>The driver will receive login credentials via SMS/Email</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 font-bold text-xs">2</span>
              </div>
              <div>
                <p className="font-medium">App Installation</p>
                <p>Driver should install the mobile app and complete profile</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-purple-600 font-bold text-xs">3</span>
              </div>
              <div>
                <p className="font-medium">Ready for Assignment</p>
                <p>Driver will be available for ambulance assignments</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverRegistrationSuccess;
